import type { ReactNode } from 'react';

/**
 * Typography wrapper for MDX-rendered post bodies.
 *
 * MDX compiles markdown to plain HTML elements (`<h2>`, `<p>`,
 * `<ul>`, …); this wrapper styles those children via Tailwind
 * arbitrary-descendant selectors so the post bodies pick up the
 * landing's voice without each .mdx file having to import a
 * specific component for every HTML primitive.
 *
 * Width is capped at `max-w-prose` (68ch in the project's
 * Tailwind theme) — the documented optimum line length for
 * extended reading. Everything inside this container reads
 * against that constraint; wider artefacts (full-bleed
 * screenshots, comparison tables) should be rendered OUTSIDE
 * `<Prose>` on the page that hosts the post.
 *
 * The class list is intentionally one long literal so Tailwind's
 * content scanner sees every utility statically — splitting via
 * runtime `join` or dynamic concatenation hides classes from
 * the JIT.
 */
const PROSE_CLASSES =
  'mx-auto max-w-prose text-fg/85 ' +
  '[&_h1]:mt-0 [&_h1]:text-4xl [&_h1]:font-medium [&_h1]:tracking-tight [&_h1]:text-fg md:[&_h1]:text-5xl ' +
  '[&_h2]:mt-14 [&_h2]:scroll-mt-24 [&_h2]:text-2xl [&_h2]:font-medium [&_h2]:tracking-tight [&_h2]:text-fg md:[&_h2]:text-3xl ' +
  '[&_h3]:mt-10 [&_h3]:scroll-mt-24 [&_h3]:text-xl [&_h3]:font-medium [&_h3]:text-fg ' +
  '[&_h4]:mt-8 [&_h4]:scroll-mt-24 [&_h4]:text-lg [&_h4]:font-medium [&_h4]:text-fg ' +
  '[&_p]:mt-5 [&_p]:leading-relaxed ' +
  '[&_a]:text-fg [&_a]:underline [&_a]:decoration-border [&_a]:underline-offset-4 hover:[&_a]:decoration-accent ' +
  '[&_ul]:mt-5 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6 ' +
  '[&_ol]:mt-5 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-6 ' +
  '[&_li]:leading-relaxed [&_li]:pl-1 ' +
  '[&_blockquote]:mt-6 [&_blockquote]:border-l-2 [&_blockquote]:border-accent [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted ' +
  '[&_:not(pre)>code]:font-mono [&_:not(pre)>code]:text-[0.92em] [&_:not(pre)>code]:rounded [&_:not(pre)>code]:border [&_:not(pre)>code]:border-border [&_:not(pre)>code]:bg-bg/60 [&_:not(pre)>code]:px-1.5 [&_:not(pre)>code]:py-0.5 [&_:not(pre)>code]:text-fg ' +
  '[&_pre]:mt-6 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:border [&_pre]:border-border [&_pre]:bg-bg/60 [&_pre]:p-4 [&_pre]:font-mono [&_pre]:text-sm [&_pre]:leading-relaxed [&_pre]:text-fg ' +
  '[&_hr]:my-12 [&_hr]:border-border ' +
  '[&_img]:mt-8 [&_img]:rounded-md [&_img]:border [&_img]:border-border ' +
  '[&_strong]:font-medium [&_strong]:text-fg ' +
  '[&_em]:italic ' +
  '[&_table]:mt-6 [&_table]:w-full [&_table]:text-sm ' +
  '[&_th]:border-b [&_th]:border-border [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-medium [&_th]:text-fg ' +
  '[&_td]:border-b [&_td]:border-border/60 [&_td]:px-3 [&_td]:py-2';

export function Prose({ children }: { readonly children: ReactNode }): JSX.Element {
  return <div className={PROSE_CLASSES}>{children}</div>;
}
